import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import inactivityTimeout from '../utils/inactivityTimeout';
import {
  Box,
  Container,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Paper,
  Button,
  CircularProgress,
  Alert,
  useTheme,
  ThemeProvider,
  createTheme,
  StepContent,
  IconButton,
  Card,
  CardContent,
  Grid,
  Divider,
  useMediaQuery
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ServiceSelection from './ServiceSelection';
import PractitionerSelection from './PractitionerSelection';
import TimezoneSelection from './TimezoneSelection';
import TimeSelection from './TimeSelection';
import PatientForm from './PatientForm';
import PaymentForm from './PaymentForm';
import Confirmation from './Confirmation';
import MobileBookingPage from './mobile/MobileBookingPage';
import {
  getClinicInfo,
  getClinicServices,
  getAvailableTimes,
  createBooking,
  getPractitioners,
  formatPrice,
  sendBloodTestEmail
} from '../services/bookingService';
import { useBloodTestParams } from './hooks/useBloodTestParams';

const steps = ['Select Service', 'Select Practitioner', 'Select Timezone', 'Choose Date & Time', 'Your Details', 'Payment', 'Confirmation'];

// Wrapper component that detects mobile and routes accordingly
const BookingPageWrapper = () => {
  const theme = useTheme();
  const [searchParams] = useSearchParams();

  // Detect mobile: screen width < 768px OR touch device
  const isMobileScreen = useMediaQuery('(max-width: 768px)');
  const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

  // URL param to force desktop or mobile view
  const forceView = searchParams.get('view'); // ?view=mobile or ?view=desktop

  // Determine which view to show
  const useMobileView = forceView === 'mobile' ||
    (forceView !== 'desktop' && (isMobileScreen || (isTouchDevice && isMobileScreen)));

  // Use the new mobile booking page for mobile devices
  if (useMobileView) {
    return <MobileBookingPage />;
  }

  // Use the original desktop booking page
  return <BookingPage />;
};

const BookingPage = () => {
  // Get URL parameters
  const { clinicStub } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const serviceId = searchParams.get('service');
  const category = searchParams.get('category');
  const returnUrl = searchParams.get('returnUrl');
  const displayMode = searchParams.get('display'); // URL override for display mode
  const showInfo = searchParams.get('showInfo') === 'true';  // Auto-show info dialog for pre-selected service

  // Blood test parameters from kiosk
  const bloodTestParams = useBloodTestParams();

  // Blood test email status: 'pending' | 'sending' | 'sent' | 'failed'
  const [bloodTestEmailStatus, setBloodTestEmailStatus] = useState('pending');
  
  // Theme setup
  const defaultTheme = useTheme();
  const [customTheme, setCustomTheme] = useState(defaultTheme);
  
  // State
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [clinic, setClinic] = useState(null);
  const [services, setServices] = useState([]);
  const [practitioners, setPractitioners] = useState([]);
  const [availableTimes, setAvailableTimes] = useState([]);
  
  // Check if we're on a payment completion page
  const [isPaymentComplete, setIsPaymentComplete] = useState(false);

  // Firefox browser detection for payment compatibility warning
  const [isFirefox, setIsFirefox] = useState(false);
  const [dismissedFirefoxWarning, setDismissedFirefoxWarning] = useState(false);
  
  // Detect user's browser timezone for default
  const detectBrowserTimezone = () => {
    try {
      const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      // Common Australian/NZ timezones that we support
      const supportedTimezones = [
        'Australia/Sydney', 'Australia/Melbourne', 'Australia/Brisbane', 
        'Australia/Adelaide', 'Australia/Perth', 'Australia/Hobart', 
        'Australia/Darwin', 'Australia/Canberra', 'Pacific/Auckland', 
        'Pacific/Chatham'
      ];
      
      if (supportedTimezones.includes(browserTimezone)) {
        console.log('Using detected browser timezone:', browserTimezone);
        return browserTimezone;
      } else {
        console.log('Browser timezone not supported, using Adelaide default:', browserTimezone);
        return 'Australia/Adelaide';
      }
    } catch (error) {
      console.warn('Failed to detect browser timezone:', error);
      return 'Australia/Adelaide';
    }
  };

  // Booking data
  const [bookingData, setBookingData] = useState({
    service: null,
    practitioner: null,
    timezone: detectBrowserTimezone(), // Auto-detect user's timezone
    appointmentTime: null,
    patient: {
      first_name: '',
      last_name: '',
      email: '',
      phone: ''
    },
    notes: '',
    payment: {
      status: 'pending'
    }
  });
  
  // Detect Firefox browser for payment compatibility warning
  useEffect(() => {
    const userAgent = navigator.userAgent.toLowerCase();
    const isFirefoxBrowser = userAgent.includes('firefox');
    setIsFirefox(isFirefoxBrowser);
    if (isFirefoxBrowser) {
      console.warn('Firefox detected - payment processing may have compatibility issues');
    }
  }, []);

  // Initialize inactivity timeout on component mount
  useEffect(() => {
    inactivityTimeout.init();
    
    // Cleanup on unmount
    return () => {
      inactivityTimeout.stop();
    };
  }, []);
  
  // Setup and check for payment completion
  useEffect(() => {
    console.log('BOOKING PAGE LOADED:');
    console.log('- Current URL:', window.location.href);
    console.log('- Service ID:', serviceId);
    console.log('- Return URL:', returnUrl);
    
    // Check if we're on a payment completion page
    const isComplete = window.location.href.includes('payment-complete') || 
                     window.location.href.includes('success') ||
                     window.location.href.includes('AccessCode=') ||
                     window.location.href.includes('session_id=');
    setIsPaymentComplete(isComplete);
    console.log('- Is payment complete page:', isComplete);
    
    // If we're on a payment completion page, determine the right step
    if (isComplete) {
      // Skip the confirmation page entirely - go directly to success
      setActiveStep(7); // Success step
      console.log('- Skipping confirmation, setting to success step automatically');
      
      // Extract payment details from URL if available
      const urlParams = new URLSearchParams(window.location.search);
      const accessCode = urlParams.get('AccessCode');
      const transactionId = urlParams.get('id') || urlParams.get('transaction_id');
      const sessionId = urlParams.get('session_id');
      const success = urlParams.get('success') === 'true';
      
      // Get appointment details if they're in the URL (comes from automatic booking)
      const appointmentId = urlParams.get('appointment_id');
      
      // If we're on the success route with appointment_id, update payment status accordingly
      if (appointmentId && window.location.href.includes('success')) {
        console.log('- Found appointment ID in URL:', appointmentId);
        setBookingData(prev => ({
          ...prev,
          payment: {
            ...prev.payment,
            status: 'complete',
            appointment_created: true,
            appointment_id: appointmentId,
            transactionId: transactionId || sessionId || 'payment-complete'
          }
        }));
      }
      
      // Try to retrieve booking data from localStorage
      try {
        const bookingDataStr = localStorage.getItem('currentBooking');
        console.log('- Retrieved localStorage booking data:', bookingDataStr);
        
        if (bookingDataStr) {
          const storedBookingInfo = JSON.parse(bookingDataStr);
          
          // Set the clinicStub from localStorage if we don't have it from URL params
          if (!clinicStub && storedBookingInfo.clinicStub) {
            console.log('- Setting clinicStub from localStorage:', storedBookingInfo.clinicStub);
            // We can't directly modify clinicStub from useParams, but we can set it in state for use later
            window.history.replaceState(null, '', `/${storedBookingInfo.clinicStub}/booking/payment-complete${window.location.search}`);
            // Force reload to get the updated URL params
            window.location.reload();
            return;
          }
          
          // In payment-complete path, we need to fully restore all saved booking data
          // This ensures we don't get "Booking information is incomplete" error
          try {
            // Try to get saved booking session from sessionStorage
            const savedSession = sessionStorage.getItem('bookingSession');
            console.log('- Retrieved booking session from sessionStorage:', savedSession ? 'yes' : 'no');
            
            if (savedSession) {
              const fullBookingData = JSON.parse(savedSession);
              console.log('- Restoring full booking data from session storage');
              setBookingData(fullBookingData);
            }
          } catch (sessErr) {
            console.error('- Error retrieving from sessionStorage:', sessErr);
          }
          
          // If we have a Stripe session ID, check the payment status from the API
          if (sessionId) {
            console.log('- Found Stripe session ID in URL, checking payment status:', sessionId);
            
            // Update payment info immediately (we'll update more details after API call)
            setBookingData(prev => ({
              ...prev,
              payment: {
                ...prev.payment,
                status: 'complete',
                transactionId: sessionId
              }
            }));
            
            // Check payment status from the server
            const { checkPaymentStatus } = require('../services/bookingService');
            checkPaymentStatus(sessionId, 'stripe', storedBookingInfo.clinicId)
              .then(status => {
                console.log('- Payment status check result:', status);
                
                // If the appointment was created server-side, skip to success step
                if (status.appointment_created) {
                  console.log('- Appointment already created server-side, skipping to success step');
                  setActiveStep(7); // Skip to success step
                } else {
                  // Appointment hasn't been created yet - create it automatically
                  console.log('- Appointment not yet created, creating it automatically');
                  handleConfirmBooking();
                }
                
                // Update booking data with more details from the payment status
                setBookingData(prev => ({
                  ...prev,
                  payment: {
                    ...prev.payment,
                    status: 'complete',
                    transactionId: sessionId,
                    details: status
                  }
                }));
              })
              .catch(error => {
                console.error('- Error checking payment status:', error);
              });
          } else if (accessCode || transactionId) {
            // For eWay payments with an access code
            console.log('- Found eWay access code or transaction ID, creating appointment automatically');
            
            // Update payment info immediately 
            setBookingData(prev => ({
              ...prev,
              payment: {
                ...prev.payment,
                status: 'complete',
                accessCode,
                transactionId
              }
            }));
            
            // Automatically create the appointment without requiring confirmation
            // Need a small timeout to ensure booking data is loaded first
            setTimeout(() => {
              if (bookingData && bookingData.service) {
                console.log('- Creating appointment automatically for eWay payment');
                handleConfirmBooking();
              } else {
                console.log('- Waiting for booking data to be loaded before creating appointment');
                // Try again after more data is loaded
                setTimeout(() => handleConfirmBooking(), 2000);
              }
            }, 500);
            setBookingData(prev => ({
              ...prev,
              payment: {
                ...prev.payment,
                status: 'complete',
                accessCode,
                transactionId
              }
            }));
          }
        } else {
          console.warn('- No booking data found in localStorage');
        }
      } catch (e) {
        console.error('- Error processing localStorage data:', e);
      }
    }
  }, [clinicStub]);
  
  // Load clinic data on component mount
  useEffect(() => {
    // If we're on the admin path, don't try to fetch clinic data
    if (clinicStub === 'admin') {
      console.log('Admin path detected, skipping clinic fetch');
      setLoading(false);
      return;
    }
    
    const fetchClinic = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Get clinic info by stub
        const clinicResult = await getClinicInfo(null, clinicStub);
        
        if (!clinicResult || !clinicResult.clinic) {
          throw new Error('Clinic not found');
        }
        
        setClinic(clinicResult.clinic);
        
        // Create custom theme with clinic's primary color
        if (clinicResult.clinic.primary_color) {
          setCustomTheme(createTheme({
            ...defaultTheme,
            palette: {
              ...defaultTheme.palette,
              primary: {
                main: clinicResult.clinic.primary_color,
              },
            },
          }));
        }
        
        // Get services for this clinic, passing the category parameter
        console.log(`Fetching services for clinic ID ${clinicResult.clinic.id}${category ? ` in category '${category}'` : ''}`);
        const servicesResult = await getClinicServices(clinicResult.clinic.id, category);
        
        // servicesResult is already an array, not wrapped in an appointment_types property
        setServices(servicesResult || []);
        console.log(`Retrieved ${servicesResult.length} services${category ? ` in category '${category}'` : ''}:`, servicesResult);
        
        // Get practitioners for this clinic
        const practitionersResult = await getPractitioners(clinicResult.clinic.id);
        setPractitioners(practitionersResult.practitioners || []);
        
        // If serviceId is provided, pre-select that service
        if (serviceId && servicesResult) {
          const selectedService = servicesResult.find(
            service => service.id.toString() === serviceId.toString()
          );

          if (selectedService) {
            console.log('Pre-selecting service:', selectedService.name);

            // If showInfo=true, stay on service step and let the info dialog show first
            if (showInfo) {
              console.log('showInfo mode: staying on service step to show info dialog');
              // Don't advance - ServiceSelection component will handle showing dialog
            } else {
              setBookingData(prev => ({
                ...prev,
                service: selectedService
              }));

              // If we're auto-selecting a service, advance to the next step
              if (!isPaymentComplete) {
                setActiveStep(1); // Go to practitioner selection
              }
            }
          } else {
            console.log(`Service ID ${serviceId} not found in available services`);
          }
        }
        
      } catch (err) {
        console.error('Error loading clinic data:', err);
        setError(err.message || 'Failed to load clinic data');
      } finally {
        setLoading(false);
      }
    };
    
    fetchClinic();
  }, [clinicStub, serviceId, category, isPaymentComplete]);
  
  // Load available times when practitioner and service are selected
  useEffect(() => {
    // Skip fetching times if we're on the payment completion page
    if (isPaymentComplete) {
      console.log('On payment completion page - skipping available times fetch');
      return;
    }
    
    const fetchAvailableTimes = async () => {
      if (!bookingData.service || !bookingData.practitioner || !clinic) return;
      
      // If we already have a selected time, don't fetch again
      if (bookingData.appointmentTime) {
        console.log('Appointment time already selected, skipping available times fetch');
        return;
      }
      
      try {
        setLoading(true);
        setError(null); // Clear any previous errors
        
        // Get the current date - IMPORTANT: Use actual current date, not future year
        const today = new Date();
        // Fix the year to current year, not 2025
        const currentYear = today.getFullYear();
        today.setFullYear(currentYear);
        
        console.log(`Using CURRENT date and time: ${today.toISOString()}`);
        const allAvailableTimes = [];
        
        // IMPORTANT: Cliniko API works best with requests of one day at a time
        // The API documentation states the time frame cannot exceed 7 days
        // We'll make up to 7 requests to cover a week, which is safer
        
        console.log('Fetching available times for a one-week period');
        console.log('Using service ID:', bookingData.service.id);
        console.log('Using clinic ID:', clinic.id);
        console.log('Using business ID:', clinic.business_id);
        
        // "Any Practitioner" option handling
        if (bookingData.practitioner.id === 'any') {
          console.log('Any practitioner selected - fetching times for all clinic practitioners');
          // Get the list of practitioners from the clinic
          const practitionerIds = clinic.practitioner_ids || [];
          console.log(`Found ${practitionerIds.length} practitioners for clinic`);
          
          // Track practitioners with available times
          const practitionersWithTimes = new Set();
          
          // Fetch individual practitioner data for display
          const practitionerData = {};
          for (const practitioner of practitioners) {
            practitionerData[practitioner.id] = practitioner.name;
          }
          
          // Process each day for each practitioner
          const totalDays = 7;
          for (const practitionerId of practitionerIds) {
            console.log(`Checking for times with practitioner ID: ${practitionerId}`);
            
            for (let i = 0; i < totalDays; i++) {
              try {
                // Calculate date for this window
                const day = new Date(today);
                day.setDate(today.getDate() + i);
                
                // Format as YYYY-MM-DD
                const padZero = (num) => num < 10 ? `0${num}` : `${num}`;
                const dateStr = `${day.getFullYear()}-${padZero(day.getMonth() + 1)}-${padZero(day.getDate())}`;
                
                // Get available times for this practitioner on this day
                const dayResult = await getAvailableTimes({
                  clinic_id: clinic.id.toString(),
                  appointment_type: bookingData.service.id,
                  practitioner_id: practitionerId,
                  from: dateStr,
                  to: dateStr
                }, bookingData.timezone);
                
                // Process available times
                if (dayResult && dayResult.available_times && dayResult.available_times.length > 0) {
                  // Keep track of practitioners who have available times
                  practitionersWithTimes.add(practitionerId);
                  
                  // Store the practitioner info with each time slot
                  dayResult.available_times.forEach(time => {
                    // Add the actual practitioner ID that will be used for booking
                    time.original_practitioner_id = practitionerId;
                    allAvailableTimes.push(time);
                  });
                }
              } catch (dayError) {
                console.error(`Error processing day ${i+1} for practitioner ${practitionerId}:`, dayError);
              }
              
              // Add a small delay between API calls
              if (i < totalDays - 1) {
                await new Promise(resolve => setTimeout(resolve, 300));
              }
            }
          }
          
          console.log(`Found times for ${practitionersWithTimes.size} practitioners out of ${practitionerIds.length}`);
        } else {
          // Regular single practitioner flow
          console.log('Using specific practitioner ID:', bookingData.practitioner.id);
          
          // Show warning about Cliniko API limitations
          console.log('IMPORTANT CLINIKO API LIMITATIONS:');
          console.log('- The business must be enabled for online bookings');
          console.log('- The appointment type must be enabled for online bookings');
          console.log('- The practitioner must be enabled for online bookings');
        
          // Process each day window (up to 7 days)
          const totalDays = 7; // One week is safer
          let successCount = 0;
        
          for (let i = 0; i < totalDays; i++) {
            try {
              // Calculate date for this window (single day)
              const day = new Date(today);
              day.setDate(today.getDate() + i);
              
              // Format as YYYY-MM-DD
              const padZero = (num) => num < 10 ? `0${num}` : `${num}`;
              const dateStr = `${day.getFullYear()}-${padZero(day.getMonth() + 1)}-${padZero(day.getDate())}`;
              
              console.log(`Fetching day ${i+1}/${totalDays}: ${dateStr}`);
              
              // Fetch available times for this day (use same date for from and to)
              let retry = 0;
              let dayResult = null;
              
              while (retry < 2 && dayResult === null) {
                try {
                  dayResult = await getAvailableTimes({
                    clinic_id: clinic.id.toString(),
                    appointment_type: bookingData.service.id,
                    practitioner_id: bookingData.practitioner.id,
                    from: dateStr,
                    to: dateStr // Same day for from and to
                  }, bookingData.timezone);
                  
                  // Add times from this day to our full result set
                  // Note: dayResult.available_times should always exist but might be empty
                  if (dayResult && dayResult.available_times) {
                    const count = dayResult.available_times.length;
                    
                    if (count > 0) {
                      console.log(`Day ${i+1}: Found ${count} available times`);
                      
                      // Add practitioner info to each time slot
                      const timesWithPractitioner = dayResult.available_times.map(time => ({
                        ...time,
                        original_practitioner_id: bookingData.practitioner.id
                      }));
                      
                      allAvailableTimes.push(...timesWithPractitioner);
                    } else {
                      console.log(`Day ${i+1}: No available times found (empty array)`);
                    }
                    
                    // Count as success even if no times found - at least the API call worked
                    successCount++;
                  } else {
                    console.warn(`Day ${i+1}: Unexpected response format`);
                    console.warn('Response structure:', dayResult ? Object.keys(dayResult) : 'null');
                  }
                } catch (retryError) {
                  console.error(`Day ${i+1}, Attempt ${retry + 1} failed:`, retryError);
                  retry++;
                  if (retry >= 2) {
                    console.error(`Day ${i+1}: Failed after multiple attempts`);
                  } else {
                    await new Promise(resolve => setTimeout(resolve, 1000)); // Wait 1 second before retry
                  }
                }
              }
              
              // Note: Removed early termination logic to ensure we check all 7 days
              // Some practitioners may have booking lead times that push availability further out
              
              // Add a small delay between API calls to avoid rate limiting
              if (i < totalDays - 1) {
                await new Promise(resolve => setTimeout(resolve, 300));
              }
            } catch (dayError) {
              console.error(`Error processing day ${i+1}:`, dayError);
              // Continue to next day even if this one fails
            }
          }
        }
        
        // Create a result object similar to what the API would return
        const timesResult = { available_times: allAvailableTimes };
        console.log(`Total available times found across all windows: ${allAvailableTimes.length}`);
        
        if (!timesResult || !timesResult.available_times) {
          console.warn('No available times found or invalid response format');
          setAvailableTimes([]);
        } else {
          let sortedTimes = timesResult.available_times.sort((a, b) => {
            return new Date(a.iso) - new Date(b.iso);
          });
          
          // Deduplicate times when "any practitioner" is selected
          if (bookingData.practitioner.id === 'any') {
            console.log(`Deduplicating ${sortedTimes.length} times from multiple practitioners`);
            
            const timeSlotMap = new Map();
            
            sortedTimes.forEach(time => {
              const timeKey = time.iso; // Use ISO timestamp as unique key
              
              if (!timeSlotMap.has(timeKey)) {
                // First time we see this slot - keep it
                timeSlotMap.set(timeKey, time);
              } else {
                // Duplicate time slot - choose the "best" one (e.g., alphabetically first practitioner)
                const existing = timeSlotMap.get(timeKey);
                const currentPractitionerName = time.practitioner_name || '';
                const existingPractitionerName = existing.practitioner_name || '';
                
                if (currentPractitionerName < existingPractitionerName) {
                  // Current practitioner comes first alphabetically
                  timeSlotMap.set(timeKey, time);
                }
              }
            });
            
            sortedTimes = Array.from(timeSlotMap.values()).sort((a, b) => {
              return new Date(a.iso) - new Date(b.iso);
            });
            
            console.log(`After deduplication: ${sortedTimes.length} unique time slots`);
          }
          
          console.log(`Found ${sortedTimes.length} available times`);
          setAvailableTimes(sortedTimes);
        }
      } catch (err) {
        console.error('Error loading available times:', err);
        setError('Unable to load available appointment times. Please try again later or contact the clinic directly.');
        setAvailableTimes([]);
      } finally {
        setLoading(false);
      }
    };
    
    if (bookingData.service && bookingData.practitioner && !isPaymentComplete) {
      fetchAvailableTimes();
    }
  }, [bookingData.service, bookingData.practitioner, clinic, practitioners, isPaymentComplete, bookingData.appointmentTime]);
  
  const handleNext = () => {
    setActiveStep((prevStep) => prevStep + 1);
  };
  
  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };
  
  const handleServiceSelect = async (service) => {
    setBookingData({
      ...bookingData,
      service,
      practitioner: null, // Reset practitioner when service changes
      appointmentTime: null // Reset time selection when service changes
    });
    
    // Fetch practitioners who can provide this service
    if (clinic && service) {
      try {
        setLoading(true);
        console.log(`Fetching practitioners for service ID: ${service.id}`);
        
        // Get practitioners for this service
        const practitionersResult = await getPractitioners(clinic.id, service.id);
        setPractitioners(practitionersResult.practitioners || []);
        console.log(`Filtered practitioners for service ${service.name}: ${practitionersResult.practitioners?.length || 0}`);
      } catch (error) {
        console.error('Error loading practitioners for service:', error);
        setError('Failed to load practitioners for this service');
      } finally {
        setLoading(false);
      }
    }
    
    handleNext();
  };
  
  const handlePractitionerSelect = (practitioner) => {
    setBookingData({
      ...bookingData,
      practitioner,
      appointmentTime: null // Reset time selection when practitioner changes
    });
    handleNext();
  };
  
  const handleTimezoneSelect = (timezone) => {
    setBookingData({
      ...bookingData,
      timezone
    });
    handleNext();
  };
  
  const handleTimeSelect = (time) => {
    console.log('handleTimeSelect called with time:', time);
    console.log('Current practitioner:', bookingData.practitioner);
    
    // If this is from the "Any Practitioner" option, we need to use the actual practitioner ID
    // from the time slot for payment processing and appointment booking
    if (bookingData.practitioner.id === 'any' && time.practitioner_id) {
      console.log('Using practitioner from time slot:', time.practitioner_id);
      
      // Find the actual practitioner in our list
      const actualPractitioner = practitioners.find(p => p.id === time.practitioner_id);
      console.log('Found actual practitioner:', actualPractitioner);
      
      // Use the actual practitioner for the appointment and payment processing
      setBookingData({
        ...bookingData,
        practitioner: actualPractitioner || bookingData.practitioner,
        appointmentTime: time
      });
    } else {
      setBookingData({
        ...bookingData,
        appointmentTime: time
      });
    }
    handleNext();
  };
  
  // Handle blood test email sending (async, non-blocking)
  const handleBloodTestEmail = async (email) => {
    if (!bloodTestParams.bloodTestRequired || !bloodTestParams.bloodTestUrl) {
      console.log('Blood test email not required or no URL provided');
      return;
    }

    console.log('Sending blood test email to:', email);
    setBloodTestEmailStatus('sending');

    try {
      const result = await sendBloodTestEmail({
        email: email,
        bloodTestUrl: bloodTestParams.bloodTestUrl,
        bloodTestType: bloodTestParams.bloodTestType,
        bloodTestPrice: bloodTestParams.bloodTestPrice
      });

      if (result.success) {
        console.log('Blood test email sent successfully');
        setBloodTestEmailStatus('sent');
      } else {
        console.error('Blood test email failed:', result.error);
        setBloodTestEmailStatus('failed');
      }
    } catch (error) {
      console.error('Blood test email error:', error);
      setBloodTestEmailStatus('failed');
    }
  };

  const handlePatientInfoSubmit = (patientData, notes) => {
    // Enhanced debug logging
    console.log('%c PATIENT FORM SUBMISSION - STARTING', 'background: #ff0000; color: white; font-size: 16px; padding: 5px;');
    console.log('Current step:', activeStep);
    console.log('Current booking data:', JSON.stringify(bookingData, null, 2));
    console.log('Service price type:', typeof bookingData.service.price);
    console.log('Service price value:', bookingData.service.price);
    console.log('Clinic stub:', clinicStub);

    // Send blood test email if required (async, non-blocking)
    if (bloodTestParams.bloodTestRequired && patientData.email) {
      handleBloodTestEmail(patientData.email);
    }
    
    // First update the booking data with patient info
    const updatedBookingData = {
      ...bookingData,
      patient: patientData,
      notes
    };
    
    // We no longer have any "free clinics" - each service is evaluated based on its price
    // All clinics can have both free and paid services
    
    // Check if this is a free appointment (price is 0 or NULL or equivalent)
    const servicePrice = bookingData.service?.price;
    
    // Comprehensive check for "free" appointment
    // 1. First convert any string to a number if possible
    let numericPrice = servicePrice;
    if (typeof servicePrice === 'string') {
      numericPrice = parseFloat(servicePrice);
      // If parsing failed (NaN), keep the original value
      if (isNaN(numericPrice)) {
        numericPrice = servicePrice;
      }
    }
    
    // 2. Now do the actual comparison with the numeric value if possible
    const isFreeAppointment = 
      servicePrice === null || 
      servicePrice === 0 || 
      servicePrice === '0' || 
      numericPrice === 0 ||
      numericPrice === 0.0;
    
    console.log('%c DETAILED PRICE ANALYSIS', 'background: #ff8c00; color: black; font-size: 16px; padding: 5px;');
    console.log('- Original price value:', servicePrice);
    console.log('- Original price type:', typeof servicePrice);
    console.log('- Numeric price after conversion:', numericPrice);
    console.log('- Is null check:', servicePrice === null);
    console.log('- Is 0 check:', servicePrice === 0);
    console.log('- Is "0" string check:', servicePrice === '0');
    console.log('- Is numeric 0 check:', numericPrice === 0);
    console.log('- Final free appointment status:', isFreeAppointment);
    
    if (isFreeAppointment) {
      console.log('%c FREE APPOINTMENT DETECTED - SKIPPING PAYMENT STEP', 'background: #00ff00; color: black; font-size: 16px; padding: 5px;');
      
      // Add payment information for free appointments
      updatedBookingData.payment = {
        status: 'complete',
        transactionId: 'free-appointment'
      };
      
      // Save booking session data to sessionStorage for confirmation page
      try {
        sessionStorage.setItem('bookingSession', JSON.stringify(updatedBookingData));
        console.log('Saved free appointment booking data to session storage:', 
          JSON.stringify(updatedBookingData, null, 2));
        
        // Also save minimal booking info to localStorage for after state reset
        const clinicStub = window.location.pathname.split('/')[1]; // The first segment after the domain
        localStorage.setItem('currentBooking', JSON.stringify({
          clinicId: clinic.id,
          clinicStub: clinicStub,
          serviceId: bookingData.service.id,
          practitionerId: bookingData.practitioner.id
        }));
        console.log('Saved minimal booking info to localStorage');
      } catch (e) {
        console.error('Error saving to storage:', e);
      }
      
      // Log state before updating
      console.log('About to update state and navigate to confirmation step');
      
      // Update state with all the data in one go
      setBookingData(updatedBookingData);
      
      // Log that we're about to change the step
      console.log('About to set active step to 5 (confirmation)');
      
      // Skip to confirmation step (index 6)
      setActiveStep(7);
      
      // Log after step change
      console.log('%c STEP CHANGED TO 5 - CONFIRMATION COMPONENT', 'background: #0000ff; color: white; font-size: 16px; padding: 5px;');
      console.log('Current component at step 5:', renderStepContent(5));
    } else {
      // For paid appointments, just update booking data and proceed to payment
      console.log('%c PAID APPOINTMENT DETECTED - PROCEEDING TO PAYMENT STEP', 'background: #ff9900; color: black; font-size: 16px; padding: 5px;');
      console.log('Price value:', servicePrice, 'Price type:', typeof servicePrice);
      setBookingData(updatedBookingData);
      handleNext();
    }
    
    // Final log to track completion
    console.log('PATIENT FORM SUBMISSION - COMPLETED');
  };
  
  const handlePaymentSuccess = (paymentData) => {
    console.log('Payment success handler called with data:', paymentData);

    setBookingData({
      ...bookingData,
      payment: {
        status: 'complete',
        transactionId: paymentData.transactionId,
        accessCode: paymentData.accessCode,
        // Store appointment details if provided (for eWay Pay Now)
        appointment_details: paymentData.appointment_details,
        booking_id: paymentData.booking_id,
        // Store appointment creation status
        appointment_created: paymentData.appointment_created,
        appointment_id: paymentData.appointment_id
      }
    });

    // Check if appointment was already created during payment processing
    // This applies to eWay Secure Fields, eWay Pay Now, and any processor that creates appointments automatically
    if (paymentData.appointment_created) {
      console.log('Appointment already created during payment - skipping to final success page');
      console.log('Appointment ID:', paymentData.appointment_id);
      // Skip confirmation step and go directly to final success step
      setActiveStep(7);
    } else if (paymentData.processor === 'eway_paynow' && paymentData.appointment_details) {
      // Legacy check for eWay Pay Now
      console.log('eWay Pay Now payment with automatic appointment creation - skipping to final success page');
      setActiveStep(7);
    } else {
      // For other payment methods, go to confirmation step as usual
      handleNext();
    }
  };
  
  const handlePaymentCancel = () => {
    handleBack();
  };
  
  const handleConfirmBooking = async () => {
    // Check if this is a payment-complete page, with URL evidence of payment
    const isPaymentCompletePage = window.location.pathname.includes('payment-complete');
    const urlParams = new URLSearchParams(window.location.search);
    const hasSessionId = urlParams.get('session_id');
    const hasSuccess = urlParams.get('success') === 'true';
    
    if (!clinic || !bookingData.service || !bookingData.practitioner || !bookingData.appointmentTime) {
      setError('Booking information is incomplete');
      return;
    }
    
    // We no longer have any "free clinics" - each service is evaluated based on its price
    
    // Check if this is a free appointment (price is 0 or NULL)
    // Enhanced price checking
    let numericPrice = bookingData.service.price;
    if (typeof numericPrice === 'string') {
      numericPrice = parseFloat(numericPrice);
      if (isNaN(numericPrice)) {
        numericPrice = bookingData.service.price;
      }
    }
    
    const isFreeAppointment = 
      bookingData.service.price === null || 
      bookingData.service.price === 0 || 
      bookingData.service.price === '0' || 
      numericPrice === 0 ||
      numericPrice === 0.0;
    console.log('Is free appointment check:', isFreeAppointment, 'Price value:', bookingData.service.price);
    
    // On payment-complete path, we can skip the payment status check
    const paymentEvidenceInURL = (isPaymentCompletePage || hasSessionId || hasSuccess);
    const paymentStatus = bookingData.payment && bookingData.payment.status === 'complete';
    
    // Check if payment is complete, unless we're on payment-complete path or it's a free appointment
    if (!paymentStatus && !paymentEvidenceInURL && !isFreeAppointment) {
      setError('Payment must be completed before confirming booking');
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      
      // Calculate end time based on service duration - we need to calculate this since we only have the start time
      const startTime = new Date(bookingData.appointmentTime.iso);
      const endTime = new Date(startTime.getTime() + (bookingData.service.duration_in_minutes * 60000));
      
      // Get transaction ID from either booking data or URL
      const transactionId = 
        (bookingData.payment && bookingData.payment.transactionId) || 
        urlParams.get('session_id') || 
        urlParams.get('id') || 
        urlParams.get('transaction_id') || 
        'payment-completed';
      
      // Include payment information in notes
      const paymentNotes = `Payment completed: Transaction ID ${transactionId}`;
      const combinedNotes = bookingData.notes 
        ? `${bookingData.notes}\n\n${paymentNotes}` 
        : paymentNotes;
      
      console.log('Creating booking with payment evidence:', {
        isPaymentCompletePage,
        hasSessionId,
        transactionId,
        paymentStatus,
      });
      
      const bookingResult = await createBooking({
        clinic_id: clinic.id.toString(),
        appointment_type_id: bookingData.service.id,
        practitioner_id: bookingData.practitioner.id,
        starts_at: startTime.toISOString(),
        ends_at: endTime.toISOString(),
        patient_first_name: bookingData.patient.first_name,
        patient_last_name: bookingData.patient.last_name,
        patient_email: bookingData.patient.email,
        patient_phone: bookingData.patient.phone,
        notes: combinedNotes
      });
      
      // Success! Booking is confirmed
      setActiveStep(7); // Move to success step (beyond confirmation)
      
      // Clean up storage after successful booking
      try {
        sessionStorage.removeItem('bookingSession');
        localStorage.removeItem('currentBooking');
        console.log('Cleared booking data from storage after successful booking');
      } catch (e) {
        console.error('Error clearing storage:', e);
      }
      
    } catch (err) {
      console.error('Error creating booking:', err);
      setError(err.message || 'Failed to create booking');
    } finally {
      setLoading(false);
    }
  };
  
  // Determine if the next button should be disabled
  const isNextDisabled = () => {
    switch (activeStep) {
      case 0: // Service selection
        return !bookingData.service;
      case 1: // Practitioner selection
        return !bookingData.practitioner;
      case 2: // Timezone selection
        return !bookingData.timezone;
      case 3: // Time selection
        return !bookingData.appointmentTime;
      case 4: // Patient info
        return !bookingData.patient.first_name || 
               !bookingData.patient.last_name || 
               !bookingData.patient.email;
      case 5: // Payment
        // Payment step doesn't use the next button - it's handled within the payment component
        return true;
      case 6: // Confirmation
        return !bookingData.payment || bookingData.payment.status !== 'complete';
      default:
        return false;
    }
  };
  
  // Determine the effective display mode
  const getEffectiveDisplayMode = () => {
    // URL parameter takes precedence
    if (displayMode === 'grouped' || displayMode === 'flat') {
      return displayMode;
    }
    
    // Use clinic's configured display mode
    if (clinic?.display_mode) {
      return clinic.display_mode;
    }
    
    // Default to flat
    return 'flat';
  };

  // Render content based on current step
  const renderStepContent = (step) => {
    console.log(`%c RENDERING STEP CONTENT FOR STEP ${step}`, 'background: #800080; color: white; font-size: 16px; padding: 5px;');
    console.log('Current booking data:', bookingData);
    console.log('Price value:', bookingData?.service?.price);
    console.log('Price type:', typeof bookingData?.service?.price);
    
    switch (step) {
      case 0:
        console.log('Rendering ServiceSelection component');
        const effectiveDisplayMode = getEffectiveDisplayMode();
        console.log('Using display mode:', effectiveDisplayMode);
        return (
          <ServiceSelection
            services={services}
            selectedService={bookingData.service}
            onServiceSelect={handleServiceSelect}
            loading={loading}
            displayMode={effectiveDisplayMode}
            showInfoForServiceId={showInfo ? serviceId : null}
          />
        );
      case 1:
        console.log('Rendering PractitionerSelection component');
        return (
          <PractitionerSelection 
            practitioners={practitioners} 
            selectedPractitioner={bookingData.practitioner}
            onPractitionerSelect={handlePractitionerSelect}
            onBack={handleBack}
            loading={loading}
          />
        );
      case 2:
        console.log('Rendering TimezoneSelection component');
        return (
          <TimezoneSelection 
            selectedTimezone={bookingData.timezone}
            onTimezoneChange={handleTimezoneSelect}
            clinic={clinic}
            onBack={handleBack}
          />
        );
      case 3:
        console.log('Rendering TimeSelection component');
        return (
          <TimeSelection 
            availableTimes={availableTimes} 
            selectedTime={bookingData.appointmentTime}
            onTimeSelect={handleTimeSelect}
            onBack={handleBack}
            loading={loading}
          />
        );
      case 4:
        console.log('Rendering PatientForm component');
        return (
          <PatientForm
            patientInfo={bookingData.patient}
            notes={bookingData.notes}
            onSubmit={handlePatientInfoSubmit}
            onBack={handleBack}
            loading={loading}
            bloodTestRequired={bloodTestParams.bloodTestRequired}
            bloodTestPrice={bloodTestParams.bloodTestPrice}
            bloodTestUrl={bloodTestParams.bloodTestUrl}
            bloodTestType={bloodTestParams.bloodTestType}
            bloodTestEmailStatus={bloodTestEmailStatus}
          />
        );
      case 5:
        console.log('Rendering PaymentForm component');
        if (!clinic || !bookingData.service || !bookingData.practitioner || !bookingData.appointmentTime) {
          console.error('Missing required data for payment form:', {
            hasClinic: !!clinic,
            hasService: !!bookingData.service,
            hasPractitioner: !!bookingData.practitioner,
            hasAppointmentTime: !!bookingData.appointmentTime
          });
          return <Alert severity="error">Missing clinic, service, practitioner, or appointment time information</Alert>;
        }
        
        // Calculate appointment start and end times
        const startTime = new Date(bookingData.appointmentTime.iso);
        const endTime = new Date(startTime.getTime() + (bookingData.service.duration_in_minutes * 60000));
        
        // Include payment information in notes
        const bookingNotes = bookingData.notes || '';
        
        // Expose booking data for session storage
        window.bookingData = bookingData;
        
        return (
          <PaymentForm 
            clinicId={clinic.id.toString()}
            serviceId={bookingData.service.id}
            practitionerId={bookingData.practitioner.id}
            amount={bookingData.service.price !== null ? Number(bookingData.service.price) : 0}
            patientEmail={bookingData.patient.email}
            patientName={`${bookingData.patient.first_name} ${bookingData.patient.last_name}`}
            // Pass all appointment details for potential automatic creation
            appointmentDetails={{
              appointment_type_id: bookingData.service.id,
              starts_at: startTime.toISOString(),
              ends_at: endTime.toISOString(),
              patient_first_name: bookingData.patient.first_name,
              patient_last_name: bookingData.patient.last_name,
              patient_phone: bookingData.patient.phone,
              notes: bookingNotes
            }}
            returnUrl={returnUrl}
            onSuccess={handlePaymentSuccess}
            onCancel={handlePaymentCancel}
            loading={loading}
          />
        );
      case 6:
        console.log('Rendering Confirmation component with:', {
          hasBookingData: !!bookingData,
          hasClinic: !!clinic,
          hasPayment: !!(bookingData && bookingData.payment)
        });
        
        if (!bookingData) {
          console.error('bookingData is null or undefined in step 5');
        }
        
        if (!clinic) {
          console.error('clinic is null or undefined in step 5');
        }
        
        // Create a backup direct rendering of Confirmation for free appointments
        // This ensures the confirmation page renders properly even if there's an issue
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Confirm Your Booking
            </Typography>
            
            <Card variant="outlined" sx={{ mb: 3 }}>
              <CardContent>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <CheckCircleIcon color="success" sx={{ mr: 1 }} />
                      <Typography variant="h6" color="success.main">
                        {(bookingData?.service?.price === 0 || bookingData?.service?.price === null || clinicStub === 'bmd_kiosk1' || clinicStub === 'bettermd') ? 'Free Appointment' : 'Payment Received'}
                      </Typography>
                    </Box>
                    {bookingData?.payment?.transactionId && (
                      <Typography variant="body2" color="text.secondary">
                        Transaction ID: {bookingData.payment.transactionId}
                      </Typography>
                    )}
                  </Grid>
                  
                  <Grid item xs={12}>
                    <Divider sx={{ my: 1 }} />
                  </Grid>
                  
                  <Grid item xs={4}>
                    <Typography variant="body2" color="text.secondary">
                      Clinic
                    </Typography>
                  </Grid>
                  <Grid item xs={8}>
                    <Typography variant="body2">
                      {clinic?.name || 'Unknown Clinic'}
                    </Typography>
                  </Grid>
                  
                  {bookingData?.service && (
                    <>
                      <Grid item xs={4}>
                        <Typography variant="body2" color="text.secondary">
                          Service
                        </Typography>
                      </Grid>
                      <Grid item xs={8}>
                        <Typography variant="body2">
                          {bookingData.service.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {bookingData.service.duration_in_minutes} minutes
                        </Typography>
                      </Grid>
                    </>
                  )}
                  
                  {bookingData?.practitioner && (
                    <>
                      <Grid item xs={4}>
                        <Typography variant="body2" color="text.secondary">
                          Practitioner
                        </Typography>
                      </Grid>
                      <Grid item xs={8}>
                        <Typography variant="body2">
                          {bookingData.practitioner.name}
                        </Typography>
                      </Grid>
                    </>
                  )}
                  
                  {bookingData?.appointmentTime && (
                    <>
                      <Grid item xs={4}>
                        <Typography variant="body2" color="text.secondary">
                          Date & Time
                        </Typography>
                      </Grid>
                      <Grid item xs={8}>
                        <Typography variant="body2">
                          {bookingData.appointmentTime.formatted || 'Unknown time'}
                        </Typography>
                      </Grid>
                    </>
                  )}
                  
                  <Grid item xs={12}>
                    <Divider sx={{ my: 1 }} />
                  </Grid>
                  
                  {bookingData?.patient && (
                    <>
                      <Grid item xs={4}>
                        <Typography variant="body2" color="text.secondary">
                          Patient
                        </Typography>
                      </Grid>
                      <Grid item xs={8}>
                        <Typography variant="body2">
                          {bookingData.patient.first_name} {bookingData.patient.last_name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {bookingData.patient.email}
                        </Typography>
                        {bookingData.patient.phone && (
                          <Typography variant="body2" color="text.secondary">
                            {bookingData.patient.phone}
                          </Typography>
                        )}
                      </Grid>
                    </>
                  )}
                  
                  <Grid item xs={12}>
                    <Divider sx={{ my: 1 }} />
                  </Grid>
                  
                  {bookingData?.notes && (
                    <>
                      <Grid item xs={4}>
                        <Typography variant="body2" color="text.secondary">
                          Notes
                        </Typography>
                      </Grid>
                      <Grid item xs={8}>
                        <Typography variant="body2">
                          {bookingData.notes}
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Divider sx={{ my: 1 }} />
                      </Grid>
                    </>
                  )}
                  
                  <Grid item xs={4}>
                    <Typography variant="body2" color="text.secondary">
                      Total Paid
                    </Typography>
                  </Grid>
                  <Grid item xs={8}>
                    <Typography variant="h6" color="primary">
                      {(bookingData?.service?.price === 0 || bookingData?.service?.price === null || clinicStub === 'bmd_kiosk1' || clinicStub === 'bettermd') ? 'Free' : formatPrice(bookingData?.service?.price || 0)}
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
            
            <Paper variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
              <Box>
                <Typography variant="body1" paragraph>
                  Please confirm your booking to finalize your appointment. An email confirmation will be sent to you.
                </Typography>
                
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  onClick={handleConfirmBooking}
                  disabled={loading}
                  startIcon={loading && <CircularProgress size={20} />}
                >
                  {loading ? 'Processing...' : 'Confirm Booking'}
                </Button>
              </Box>
            </Paper>
          </Box>
        );
      case 7:
        console.log('Rendering Success component');
        return (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="h5" gutterBottom sx={{backgroundColor: 'primary.main', padding: '10px', color: 'white'}}>
              🎉 Appointment Confirmed! 🎉
            </Typography>
            <Typography>
              Your appointment has been booked successfully. You should receive a confirmation email shortly.
            </Typography>
            {bookingData.service && bookingData.practitioner && bookingData.appointmentTime && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle1">
                  {bookingData.service.name} with {bookingData.practitioner.name}
                </Typography>
                <Typography>
                  {bookingData.appointmentTime.formatted}
                </Typography>
                {bookingData.payment && bookingData.payment.transactionId && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Payment Transaction ID: {bookingData.payment.transactionId}
                  </Typography>
                )}
              </Box>
            )}
            <Box sx={{ mt: 4 }}>
              <Button 
                variant="contained"
                sx={{fontSize: '18px', padding: '15px'}}
                color="primary"
                onClick={() => {
                  console.log('Return button clicked');
                  
                  // Check for returnUrl in URL parameters
                  if (returnUrl) {
                    try {
                      const decodedUrl = decodeURIComponent(returnUrl);
                      console.log('Returning to provided URL:', decodedUrl);
                      window.location.href = decodedUrl;
                      return;
                    } catch (e) {
                      console.error('Error decoding returnUrl:', e);
                    }
                  }
                  
                  // If no returnUrl or failed to decode, try to go back to the clinic home page
                  window.location.href = `/${clinicStub}`;
                }}
              >
                Return to Home
              </Button>
            </Box>
          </Box>
        );
      default:
        console.log(`Unknown step: ${step}`);
        return null;
    }
  };
  
  // Mobile-optimized stepper for smaller screens
  const renderMobileStepper = () => (
    <Stepper activeStep={activeStep} orientation="vertical">
      {steps.map((label, index) => (
        <Step key={label}>
          <StepLabel>{label}</StepLabel>
          <StepContent>
            {renderStepContent(index)}
          </StepContent>
        </Step>
      ))}
    </Stepper>
  );
  
  // Desktop stepper for larger screens
  const renderDesktopStepper = () => (
    <>
      <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 4 }}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>
      
      {renderStepContent(activeStep)}
    </>
  );
  
  const handleBackButtonClick = () => {
    // Go back to the referring page if there is one
    if (document.referrer) {
      window.location.href = document.referrer;
    } else {
      // If no referrer, try to go back in history
      navigate(-1);
    }
  };

  return (
    <ThemeProvider theme={customTheme}>
      <Container maxWidth="md" sx={{ py: 4 }}>
        {/* Back button - only show on first step or on success page */}
        {(activeStep === 0 || activeStep === 7) && (
          <Box sx={{ position: 'absolute', left: 16, top: 16 }}>
            <IconButton 
              aria-label="back"
              onClick={handleBackButtonClick}
              sx={{ 
                bgcolor: 'rgba(0, 0, 0, 0.05)', 
                '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.1)' } 
              }}
            >
              <ArrowBackIcon />
            </IconButton>
          </Box>
        )}
        
        {loading && activeStep === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : error && activeStep !== 7 ? (
          <Alert severity="error" sx={{ mb: 4 }}>
            {error}
          </Alert>
        ) : (
          <>
            {/* Clinic header */}
            <Box sx={{ mb: 4, textAlign: 'center' }}>
              {/* Logo display - single logo centered, dual logos side by side */}
              {(clinic?.logo_url || clinic?.logo_url_secondary) && (
                <Box sx={{ mb: 2, display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 3 }}>
                  {clinic?.logo_url && (
                    <img 
                      src={clinic.logo_url} 
                      alt={`${clinic.name} primary logo`} 
                      style={{ maxHeight: 60, maxWidth: clinic?.logo_url_secondary ? '45%' : '100%' }} 
                    />
                  )}
                  {clinic?.logo_url_secondary && (
                    <img 
                      src={clinic.logo_url_secondary} 
                      alt={`${clinic.name} secondary logo`} 
                      style={{ maxHeight: 60, maxWidth: clinic?.logo_url ? '45%' : '100%' }} 
                    />
                  )}
                </Box>
              )}
              <Typography variant="h4" component="h1" gutterBottom>
                {clinic?.name || 'Medical Appointment Booking'}
              </Typography>
              <Typography variant="subtitle1" color="text.secondary">
                Book your appointment online
              </Typography>
            </Box>

            {/* Firefox browser compatibility warning */}
            {isFirefox && !dismissedFirefoxWarning && (
              <Alert
                severity="warning"
                sx={{ mb: 3 }}
                onClose={() => setDismissedFirefoxWarning(true)}
              >
                <strong>Browser Compatibility Notice:</strong> Firefox's Enhanced Tracking Protection may interfere with payment processing.
                For the best experience, we recommend using <strong>Chrome</strong> or <strong>Safari</strong> for this booking.
              </Alert>
            )}

            <Paper
              elevation={3}
              sx={{
                p: { xs: 2, sm: 3 },
                borderRadius: 2,
              }}
            >
              {/* Show different stepper based on screen size */}
              <Box sx={{ display: { xs: 'block', md: 'none' } }}>
                {renderMobileStepper()}
              </Box>
              <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                {renderDesktopStepper()}
              </Box>
            </Paper>
          </>
        )}
      </Container>
    </ThemeProvider>
  );
};

// Export the wrapper as default - it handles mobile/desktop detection
export default BookingPageWrapper;